<template>
  <component-wrapper class="my-account-new-password">
    <grid-container-fi>
      <transition-group
        enter-active-class="animated fadeIn"
        leave-active-class="animated fadeOut"
      >
        <div
          v-if="!checkForToken && !validateTokenError"
          class="fi-col-start-1 fi-col-end-13 sm:fi-col-start-3 sm:fi-col-end-11 lg:fi-col-start-5 lg:fi-col-end-9"
        >
          <div class="my-account-new-password__wrapper">
            <h1
              v-if="fields.headlineChange?.value || fields.headlineAssign?.value"
              class="my-account-new-password__headline"
            >
              {{ isDashboardRedirect ? fields.headlineChange.value : fields.headlineAssign.value }}
            </h1>

            <div class="my-account-new-password__form">
              <change-password-fi
                :hide-old-password="!isDashboardRedirect"
                @form-changed="formChanged"
              />

              <div class="field-submit">
                <loading-indicator-wrapper
                  :loading="isLoading"
                  dark
                  variant="spinnerLocal"
                  spinoff="in-relative-centered"
                  class="my-account-new-password__spinner"
                >
                  <button-fi
                    class="button-fi__primary--large button-fi--stretched"
                    type="button"
                    :disabled="!isValidInput"
                    @click.prevent="submit"
                  >
                    {{ fields.passwordButtonNewPasswordSave?.value }}
                  </button-fi>
                </loading-indicator-wrapper>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!checkForToken && validateTokenError"
          class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12 lg:fi-col-start-3 lg:fi-col-end-11"
        >
          <div
            v-if="validateTokenError"
            class="my-account-new-password__error"
          >
            <result-status-fi
              :error="errorMsg"
              :headline="fields.passwordSuccessBoxTitle"
              @error-action-clicked="goToPasswortForgot"
            />
          </div>
        </div>
        <div
          v-if="checkForToken"
          class="fi-col-span-full"
        >
          <div class="my-account-new-password__check-token">
            <loading-indicator variant="spinnerLocal" />
          </div>
        </div>
      </transition-group>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useClientHandle } from '@urql/vue';
import { ButtonFi, ChangePasswordFi, LoadingIndicator, LoadingIndicatorWrapper } from 'atoms';
import { computed, ref } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import ResultStatusFi from '@/components/result-status-fi/ResultStatusFi.vue';
import { useFischer } from '@/composables/fischerPlugin';
import { useUser } from '@/composables/user';
import MY_ACCOUNT_UPDATE_PASSWORD from '@/graphql/myAccountUpdatePassword.gql';
import MY_ACCOUNT_UPDATE_PASSWORD_LOGGED_IN_USER from '@/graphql/myAccountUpdatePasswordLoggedInUser.gql';
import VALIDATE_TOKEN from '@/graphql/validateToken.gql';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({ name: 'NewPassword' });

const props = withDefaults(
  defineProps<{
    fields?: Record<string, any>;
  }>(),
  {
    fields: () => ({}),
  },
);

const fischer = useFischer();
const { logout } = useUser();
const handle = useClientHandle();
const notificationStore = useNotificationStore();

const isLoading = ref(false);
const error = ref(false);
const validateTokenError = ref(false);
const checkForToken = ref(true);
const isValidInput = ref(false);
const form = ref<Record<string, any>>({});

const token = computed(() => fischer.query('token'));
const isDashboardRedirect = computed(() => Boolean(!token.value));

const errorMsg = computed(() => {
  if (validateTokenError.value) {
    return {
      title: props.fields?.passwordTokenErrorHeadline?.value,
      message: props.fields?.passwordTokenErrorDescription?.value,
      action: props.fields?.passwordTokenRequestNewTokenLabel?.value,
    };
  }
  return null;
});

const enterErrorState = (err: any, errorDisplay?: string) => {
  console.error(err);
  error.value = true;

  if (errorDisplay) {
    notificationStore.openToastNotification({
      message: { value: errorDisplay },
      type: 'error',
      icon: 'alert-triangle',
      persist: true,
    });
  } else {
    notificationStore.openGeneralErrorNotification();
  }
};

const submit = async () => {
  if (isValidInput.value) {
    isLoading.value = true;
    const updatePasswordParams = {
      newPassword: form.value.password,
      baseSiteId: fischer.baseSiteId(),
      token: token.value,
      lang: fischer.language_639_1(),
    };

    const updatePasswordLoggedInUserParams = {
      userId: fischer.user.id(),
      baseSiteId: fischer.baseSiteId(),
      accessToken: fischer.user.accessToken(),
      oldPassword: form.value.oldPassword,
      newPassword: form.value.password,
      lang: fischer.language_639_1(),
      division: fischer.user.division(),
      distributionChannel: fischer.user.distributionChannel(),
    };

    try {
      const { data: updateData, error: updateError } = await handle.client
        .mutation(
          isDashboardRedirect.value
            ? MY_ACCOUNT_UPDATE_PASSWORD_LOGGED_IN_USER
            : MY_ACCOUNT_UPDATE_PASSWORD,
          isDashboardRedirect.value ? updatePasswordLoggedInUserParams : updatePasswordParams,
        )
        .toPromise();

      if (updateError) {
        enterErrorState(updateError, undefined);
        isLoading.value = false;
        return;
      }

      if (updateData) {
        const success = isDashboardRedirect.value
          ? updateData.updatePasswordForLoggedInUser?.success
          : updateData.updatePassword?.success;
        if (success) {
          await logout({ reason: 'passwordChanged' });
        } else {
          enterErrorState('no mail send');
        }
      } else {
        enterErrorState('password update returned no data');
      }
    } catch (err: any) {
      enterErrorState(err, undefined);
    } finally {
      isLoading.value = false;
    }
  }
};

const validateToken = async () => {
  try {
    const { data: vtData, error: vtError } = await handle.client
      .query(
        VALIDATE_TOKEN,
        {
          baseSiteId: fischer.baseSiteId(),
          token: encodeURI(token.value ?? ''),
          division: fischer.user.division(),
          distributionChannel: fischer.user.distributionChannel(),
        },
        { requestPolicy: 'network-only' },
      )
      .toPromise();

    if (vtError) {
      validateTokenError.value = true;
      checkForToken.value = false;
      return;
    }

    if (vtData) {
      const validateTokenErrorMessage = vtData.validateToken?.errorMsg;
      if (validateTokenErrorMessage && validateTokenErrorMessage.length > 0) {
        if (validateTokenErrorMessage === 'Validation successful') {
          validateTokenError.value = false;
        } else {
          validateTokenError.value = true;
        }
      }
    }
  } catch (_error) {
    validateTokenError.value = true;
  }
  checkForToken.value = false;
};

const goToPasswortForgot = () => {
  window.location.href = fischer.pages.passwordForgotten().url;
};

const formChanged = (newForm: Record<string, any>) => {
  isValidInput.value = newForm.valid;
  form.value = newForm;
};

// created
if (token.value?.length && token.value !== '') {
  validateToken();
} else {
  checkForToken.value = false;
}
</script>

<style src="./my-account-new-password" />
